const TOOLTIP_SPACING = 10;

const handlersByTooltipKey = new Map();
let tooltipIndex = 0;

const eventOptions = { passive: false };

function nullifyEvent(e) {
  if (e.cancelable) {
    e.preventDefault();
  }

  e.stopPropagation();
  e.stopImmediatePropagation();
}

export const TooltipDirective = {
  bind: function(el, binding) {
    const { dismissOnClick, useWindowLimits } = binding.modifiers;
    const currentTooltipIndex = tooltipIndex++;

    // Tricky implementation detail!
    //
    // This flag is used to ignore the `showTooltip` call that happens when we click
    // in the original tooltip trigger ("i" icon) and there's already a tooltip opened.
    // Since the click is outside the tooltip's DOM element, the tooltip will be dismissed,
    // but since the `touchstart` event recently became non-cancelable
    // (https://developers.google.com/web/updates/2017/01/scrolling-intervention)
    // it will continue hit the trigger _again_ and show the tooltip one more time,
    // creating the experience that the tooltip is not dismiss-able.
    // We set the flag to `true` in this case and, 500ms later, set it to `false`,
    // so the trigger starts to respond to clicks/taps again.
    let ignoreTooltip = false;

    const getTooltipPosition = useWindowLimits
      ? getTooltipPositionForWindowBoundaries
      : getTooltipPositionForParentBoundaries;

    el.dataset.tooltipKey = `tooltip-${currentTooltipIndex}`;

    let hideTooltip;

    const tooltipClickHandler = dismissOnClick
      ? (e) => hideTooltip(e)
      : (e) => nullifyEvent(e);

    hideTooltip = (e) => {
      const tooltip = document.getElementById(`tooltip-${currentTooltipIndex}`);

      if (tooltip) {
        document.body.removeChild(tooltip);

        // Ensuring we don't leak handlers
        tooltip.removeEventListener("touchstart", tooltipClickHandler, eventOptions);
        tooltip.removeEventListener("mousedown", tooltipClickHandler, eventOptions);
      }

      if (e) {
        nullifyEvent(e);
      }

      document.removeEventListener("mousedown", hideTooltip, eventOptions);
      document.removeEventListener("touchstart", hideTooltip, eventOptions);
      document.defaultView.removeEventListener("resize", hideTooltip, eventOptions);

      // Now that we dismissed the tooltip, we want to prevent the trigger element, in case we
      // clicked/tapped on it again, from immediately starting the tooltip again
      ignoreTooltip = true;

      // ... and we revert the flag 500ms after that, so it goes back to work normally again
      setTimeout(() => ignoreTooltip = false, 500);
    };

    const showTooltip = () => {
      if (ignoreTooltip) {
        // Ensuring we only ignore the tooltip trigger _once_
        ignoreTooltip = false;
        return;
      }

      const tooltip = document.createElement("div");

      tooltip.id = `tooltip-${currentTooltipIndex}`;
      tooltip.className = "tooltip";
      tooltip.innerHTML = binding.value;

      tooltip.addEventListener("touchstart", tooltipClickHandler, eventOptions);
      tooltip.addEventListener("mousedown", tooltipClickHandler, eventOptions);

      const { x, y, width, height } = el.getBoundingClientRect();

      Object.assign(tooltip.style, {
        position: "absolute",
        opacity: 0,
      });

      if (el.dataset.tooltipStyle) {
        const breakpoint = matchMedia('(min-width: 1100px)').matches ? 'iPadPro' : 'iPad';
        const parsedStyle = JSON.parse(el.dataset.tooltipStyle);
        const styleToBeApplied = parsedStyle[breakpoint] ?? parsedStyle;
        Object.assign(tooltip.style, styleToBeApplied);
      }

      requestAnimationFrame(() => {
        const opacity = 1, { left, top } = getTooltipPosition(x, width, tooltip, y, height);
        Object.assign(tooltip.style, { left, top, opacity });
      });

      document.body.appendChild(tooltip);

      setTimeout(() => {
        document.addEventListener("mousedown", hideTooltip, eventOptions);
        document.addEventListener("touchstart", hideTooltip, eventOptions);
        document.defaultView.addEventListener("resize", hideTooltip, eventOptions);
      });
    };

    el.addEventListener("mousedown", showTooltip, eventOptions);

    handlersByTooltipKey.set(el.dataset.tooltipKey, { showTooltip, hideTooltip });
  },

  unbind: function (el) {
    const handlers = handlersByTooltipKey.get(el.dataset.tooltipKey);

    if (handlers) {
      // Making sure the tooltip is not visible
      handlers.hideTooltip();

      el.removeEventListener("mousedown", handlers.showTooltip, eventOptions);
    }

    handlersByTooltipKey.delete(el.dataset.tooltipKey);
  },
};

function getTooltipPositionForParentBoundaries(x, width, tooltip, y, height) {
  const parentXAndItsWidth = x + width;
  const tooltipWidth = tooltip.offsetWidth;
  const fitsHorizontallyInsideParent = parentXAndItsWidth < tooltipWidth;
  let left, top;

  if (fitsHorizontallyInsideParent) {
    left = `${x}px`;
  } else {
    left = `${parentXAndItsWidth - tooltipWidth}px`;
  }

  const parentYAndItsHeightAndSpacing = y + height + TOOLTIP_SPACING;
  const parentYMinusSpacing = window.innerHeight - TOOLTIP_SPACING;
  const tooltipHeight = tooltip.offsetHeight;
  const parentYMinusTooltipHeightMinusSpacing = y - tooltipHeight - TOOLTIP_SPACING;
  const fitsVerticallyOnScreen = parentYAndItsHeightAndSpacing + tooltipHeight < parentYMinusSpacing;

  if (fitsVerticallyOnScreen) {
    top = `${parentYAndItsHeightAndSpacing}px`;
  } else {
    top = `${parentYMinusTooltipHeightMinusSpacing}px`;
  }

  return { left, top };
}

function getTooltipPositionForWindowBoundaries(x, width, tooltip, y, height) {
  const availableWidthOnScreen = window.innerWidth - TOOLTIP_SPACING;
  const parentXAndTooltipWidth = x + tooltip.offsetWidth;
  const fitsInFrontOfParent = parentXAndTooltipWidth < availableWidthOnScreen;
  const parentXAndWidth = x + width;
  const fitsBeforeParent = parentXAndWidth - tooltip.offsetWidth > TOOLTIP_SPACING;
  let left, top;

  if (fitsInFrontOfParent) {
    left = `${x}px`;
  } else if (fitsBeforeParent) {
    left = `${parentXAndWidth - tooltip.offsetWidth}px`;
  } else {
    left = `${((window.innerWidth - tooltip.offsetWidth) / 2).toFixed()}px`;
  }

  const tooltipStartingY = y + height + TOOLTIP_SPACING;
  const tooltipStartingYAndItsHeight = tooltipStartingY + tooltip.offsetHeight;
  const availableHeightOnScreen = window.innerHeight - TOOLTIP_SPACING;
  const parentYMinusTooltipHeightMinusSpacing = y - TOOLTIP_SPACING - tooltip.offsetHeight;
  const fitsVerticallyOnScreen = tooltipStartingYAndItsHeight < availableHeightOnScreen;

  if (fitsVerticallyOnScreen) {
    top = `${tooltipStartingY}px`;
  } else {
    top = `${Math.max(TOOLTIP_SPACING, parentYMinusTooltipHeightMinusSpacing)}px`;
  }

  return { left, top };
}
